/**
 * Typed URL builders. Every tenant-scoped route lives under
 * `/tenants/:tenantCode/...` — hand-concatenating these strings throughout
 * the codebase is a refactor trap (renaming a segment = grep-and-replace
 * across 40+ files). Import from here instead.
 *
 * Convention: all helpers accept `tenantCode: string` first, then any
 * sub-route params. No optional chaining — pass an explicit value.
 */

export const paths = {
  /** Landing page — tenant picker. */
  root: '/' as const,

  /** 404 catch-all. */
  notFound: '/404' as const,

  // ── Tenant-scoped ────────────────────────────────────────────────
  tenant: (tenantCode: string): string => `/tenants/${tenantCode}`,
  dashboard: (tenantCode: string): string => `/tenants/${tenantCode}/dashboard`,
  settings: (tenantCode: string): string => `/tenants/${tenantCode}/settings`,
  // R7: `fieldDefinitions` tenant helper removed — page moved to /admin.
  // Use `paths.admin.fieldDefinitions()` instead.

  // ── Data integrations ────────────────────────────────────────────
  dataSources: (tenantCode: string): string => `/tenants/${tenantCode}/integrations/sources`,
  dataSourceEdit: (tenantCode: string, sourceCode: string): string =>
    `/tenants/${tenantCode}/integrations/sources/${sourceCode}`,
  canonicalMapping: (tenantCode: string): string => `/tenants/${tenantCode}/integrations/canonical`,

  // ── Actions / content ────────────────────────────────────────────
  actions: (tenantCode: string): string => `/tenants/${tenantCode}/actions`,
  /** The tenant's Task V2 "All Actions V2" page — redirect target when a v1-only link is hidden under mode 'task-v2'. */
  taskV2Actions: (tenantCode: string): string => `/tenants/${tenantCode}/task-v2/actions`,
  actionEdit: (tenantCode: string, taskCode: string): string =>
    `/tenants/${tenantCode}/actions/${taskCode}`,
  /** Catalogue-action detail (master library + non-master imported actions). */
  actionDetail: (tenantCode: string, actionId: string): string =>
    `/tenants/${tenantCode}/actions/detail/${actionId}`,
  /** Master-only action Library page (sidebar sub-item under Actions). */
  actionLibrary: (tenantCode: string): string => `/tenants/${tenantCode}/action-library`,
  /**
   * Catalogue-action detail opened FROM the master Library. Sibling of
   * `action-library` (not nested under `actions`) so the sidebar prefix-match
   * highlights Library, not All Actions.
   */
  actionLibraryDetail: (tenantCode: string, actionId: string): string =>
    `/tenants/${tenantCode}/action-library/detail/${actionId}`,
  adventures: (tenantCode: string): string => `/tenants/${tenantCode}/adventures`,
  sweepstakes: (tenantCode: string): string => `/tenants/${tenantCode}/sweepstakes`,
  questionnaires: (tenantCode: string): string => `/tenants/${tenantCode}/questionnaires`,
  termsOfService: (tenantCode: string): string => `/tenants/${tenantCode}/terms-of-service`,
  trivia: (tenantCode: string): string => `/tenants/${tenantCode}/trivia`,
  triviaEdit: (tenantCode: string, triviaCode: string): string =>
    `/tenants/${tenantCode}/trivia/${triviaCode}`,
  surveyEdit: (tenantCode: string, code: string): string =>
    `/tenants/${tenantCode}/surveys/${code}`,
  cmsComponents: (tenantCode: string): string => `/tenants/${tenantCode}/cms-components`,
  cmsComponentDetail: (tenantCode: string, componentCode: string): string =>
    `/tenants/${tenantCode}/cms-components/${componentCode}`,
  cmsComponentDetailV2: (tenantCode: string, componentCode: string): string =>
    `/tenants/${tenantCode}/cms-components-v2/${componentCode}`,
  htmlTemplates: (tenantCode: string): string => `/tenants/${tenantCode}/html-templates`,
  // Flex Components — per-tenant flex (flex-shell-ui) components, their variants,
  // colors, and Storyblok content authoring.
  flexComponents: (tenantCode: string): string => `/tenants/${tenantCode}/flex-components`,
  flexComponentDetail: (tenantCode: string, familyKey: string): string =>
    `/tenants/${tenantCode}/flex-components/${familyKey}`,
  flexPalette: (tenantCode: string): string => `/tenants/${tenantCode}/flex-components/palette`,
  // Storyblok-backed content (separate from the legacy CMS pages above).
  // One sidebar entry per content type — FAQs / Cards / Agreements.
  storyblokCmsComponents: (tenantCode: string): string =>
    `/tenants/${tenantCode}/storyblok-content/cms-components`,
  storyblokCmsComponentDetail: (tenantCode: string, categoryCode: string, itemId: number | string): string =>
    `/tenants/${tenantCode}/storyblok-content/cms-components/${categoryCode}/${itemId}`,
  storyblokFaqs: (tenantCode: string): string =>
    `/tenants/${tenantCode}/storyblok-content/faqs`,
  storyblokCards: (tenantCode: string): string =>
    `/tenants/${tenantCode}/storyblok-content/cards`,
  storyblokAgreements: (tenantCode: string): string =>
    `/tenants/${tenantCode}/storyblok-content/agreements`,
  notifications: (tenantCode: string): string => `/tenants/${tenantCode}/notifications`,
  // R7: `scripts` tenant helper removed — moved to `paths.admin.scripts()`.
  taskRelationships: (tenantCode: string): string => `/tenants/${tenantCode}/task-relationships`,
  memberImport: (tenantCode: string): string => `/tenants/${tenantCode}/member-import`,

  // ── Program year ─────────────────────────────────────────────────
  cohorts: (tenantCode: string): string => `/tenants/${tenantCode}/program-year/cohorts`,
  content: (tenantCode: string): string => `/tenants/${tenantCode}/program-year/content`,
  contentEdit: (tenantCode: string, componentCode: string): string =>
    `/tenants/${tenantCode}/program-year/content/${componentCode}`,
  walletTransferRules: (tenantCode: string): string =>
    `/tenants/${tenantCode}/program-year/wallet-transfer-rules`,
  rewards: (tenantCode: string): string => `/tenants/${tenantCode}/program-year/rewards`,
  communications: (tenantCode: string): string => `/tenants/${tenantCode}/program-year/comms`,
  auditLog: (tenantCode: string): string => `/tenants/${tenantCode}/program-year/audit`,
  segments: (tenantCode: string): string => `/tenants/${tenantCode}/program-year/segments`,
  currencies: (tenantCode: string): string => `/tenants/${tenantCode}/program-year/currencies`,
  lookups: (tenantCode: string): string => `/tenants/${tenantCode}/integrations/lookups`,
  dimensions: (tenantCode: string): string => `/tenants/${tenantCode}/integrations/dimensions`,

  // ── Feature flags ────────────────────────────────────────────────
  mixpanelFlags: (tenantCode: string): string => `/tenants/${tenantCode}/feature-flags`,

  // ── Ops / observability ──────────────────────────────────────────
  goLive: (tenantCode: string): string => `/tenants/${tenantCode}/go-live`,
  activity: (tenantCode: string): string => `/tenants/${tenantCode}/activity`,
  pipelines: (tenantCode: string): string => `/tenants/${tenantCode}/pipelines`,
  pipelineJobs: (tenantCode: string): string => `/tenants/${tenantCode}/pipelines/jobs`,
  consumers: (tenantCode: string): string => `/tenants/${tenantCode}/consumers`,
  consumer360: (tenantCode: string, consumerId?: string): string =>
    consumerId
      ? `/tenants/${tenantCode}/consumer360/${consumerId}`
      : `/tenants/${tenantCode}/consumer360`,
  transactions: (tenantCode: string): string => `/tenants/${tenantCode}/transactions`,
  cardOperations: (tenantCode: string): string => `/tenants/${tenantCode}/card-operations`,
  cardFunding: (tenantCode: string): string => `/tenants/${tenantCode}/card-funding`,
  healthProgram: (tenantCode: string): string => `/tenants/${tenantCode}/health-program`,
  analytics: (tenantCode: string): string => `/tenants/${tenantCode}/analytics`,
  // RBAC_V2 (R12) — takes over the Analytics slot under the flag; the old
  // `analytics` path redirects here. Same component (AnalyticsDashboard),
  // gated anyOf(insights_manager, insights_client).
  insights: (tenantCode: string): string => `/tenants/${tenantCode}/insights`,

  // ── Admin ────────────────────────────────────────────────────────
  users: (tenantCode: string): string => `/tenants/${tenantCode}/users`,
  // R7 moved this tenant helper to `paths.admin.rbac.root()`; RBAC_V2
  // (decisions §17) then retired the RBAC console entirely — `/admin/rbac`
  // now just redirects to `paths.admin.users.roles()` (see AppRoutes.tsx).
  environmentPromotion: (tenantCode: string): string =>
    `/tenants/${tenantCode}/environment-promotion`,
  // WU12 — workflow detail nested under the pipeline page.
  promotionWorkflowDetail: (tenantCode: string, workflowUuid: string): string =>
    `/tenants/${tenantCode}/environment-promotion/${workflowUuid}`,
  apiKeys: (tenantCode: string): string => `/tenants/${tenantCode}/api-keys`,
  bulkOperations: (tenantCode: string): string => `/tenants/${tenantCode}/bulk-operations`,
  fileValidation: (tenantCode: string): string => `/tenants/${tenantCode}/file-validation`,
  financialReconciliation: (tenantCode: string): string =>
    `/tenants/${tenantCode}/financial-reconciliation`,
  // R7: `secretDefaultsAudit` tenant helper removed — moved to
  // `paths.admin.secretDefaults()`.

  // ── Workflow ─────────────────────────────────────────────────────
  publish: (tenantCode: string): string => `/tenants/${tenantCode}/publish`,
  userGuide: (tenantCode: string): string => `/tenants/${tenantCode}/guide`,

  /** Access-denied page — shown when `/api/me` returns 403. */
  accessDenied: (): string => '/access-denied',

  // ── Admin view (/admin/*) ─────────────────────────────────────────
  // Super-admin only. Gated by `(isSuperAdmin && adminViewEnabled)` at
  // the component layer and by `requireRole('super-admin')` on the
  // backend. Functions even when they take no args — mirrors the tenant
  // helper convention so call sites read uniformly.
  admin: {
    root: (): string => '/admin',
    home: (): string => '/admin',
    globalCatalog: {
      root: (): string => '/admin/global-catalog',
      tasks: (code?: string): string =>
        code ? `/admin/global-catalog/tasks/${code}` : '/admin/global-catalog/tasks',
      // Adventures no longer live in the global catalog; they're tenant-scoped.
      cohorts: (code?: string): string =>
        code ? `/admin/global-catalog/cohorts/${code}` : '/admin/global-catalog/cohorts',
      sweepstakes: (code?: string): string =>
        code
          ? `/admin/global-catalog/sweepstakes/${code}`
          : '/admin/global-catalog/sweepstakes',
      termsOfService: (code?: string): string =>
        code
          ? `/admin/global-catalog/terms-of-service/${code}`
          : '/admin/global-catalog/terms-of-service',
      questionnaires: (code?: string): string =>
        code
          ? `/admin/global-catalog/questionnaires/${code}`
          : '/admin/global-catalog/questionnaires',
      collections: (code?: string): string =>
        code
          ? `/admin/global-catalog/collections/${code}`
          : '/admin/global-catalog/collections',
      planCohortPurseMappings: (code?: string): string =>
        code
          ? `/admin/global-catalog/plan-cohort-purse/${code}`
          : '/admin/global-catalog/plan-cohort-purse',
    },
    catalogs: {
      root: (): string => '/admin/catalogs',
      taskTypes: (): string => '/admin/catalogs/task-types',
      taskCategories: (): string => '/admin/catalogs/task-categories',
      taskRewardTypes: (): string => '/admin/catalogs/task-reward-types',
      walletTypes: (): string => '/admin/catalogs/wallet-types',
      notificationChannels: (): string => '/admin/catalogs/notification-channels',
      notificationCategories: (): string => '/admin/catalogs/notification-categories',
      componentTypes: (): string => '/admin/catalogs/component-types',
      onboardingComponents: (): string => '/admin/catalogs/onboarding-components',
      canonicalFields: (): string => '/admin/catalogs/canonical-fields',
      cohortAttributeDefs: (): string => '/admin/catalogs/cohort-attribute-defs',
    },
    users: {
      root: (): string => '/admin/users',
      roles: (): string => '/admin/users?tab=roles',
      groups: (): string => '/admin/users?tab=groups',
    },
    fieldDefinitions: (): string => '/admin/field-definitions',
    scripts: (): string => '/admin/scripts',
    secretDefaults: (): string => '/admin/secret-defaults-audit',
    customerDefaults: (): string => '/admin/customer-defaults',
    importExport: (): string => '/admin/import-export',
    // WAVE G — per-tenant authoring-environment overview.
    editingEnvMap: (): string => '/admin/editing-env-map',
  },
};
